import { initQueryParamPersistence } from "../lib/queryParamPersistence";

// Configuration constants
const CONFIG = {
  POLLING_INTERVAL: 1000, // Poll every 1 second
  MAX_INITIAL_RETRIES: 100, // For job status 404s
  INITIAL_RETRY_DELAY: 500,
  DEFAULT_MIN_RESPONSES: 10,
  MIN_ATTRIBUTES_REQUIRED: 2,
};

// Helper function to calculate impact value
function calculateImpact(score, overallAverage, responses) {
  return Math.abs(score - overallAverage) * Math.sqrt(responses);
}

// Helper function to calculate hotspot data with impact
function calculateHotspotData(hotspots, overallAverage) {
  if (!hotspots || hotspots.length === 0) return [];

  // First pass: Create data with impact calculations
  const hotspotsWithImpact = hotspots.map((hotspot, index) => {
    const diff = Math.round(hotspot.score - overallAverage);
    const impact = calculateImpact(
      hotspot.score,
      overallAverage,
      hotspot.responses,
    );

    return {
      originalIndex: index,
      dimension: hotspot.dimension,
      values: hotspot.values,
      score: Math.round(hotspot.score),
      responses: hotspot.responses,
      diff: diff,
      impact: impact,
      impactPercentage: 0, // Placeholder, will calculate after we know maxImpact
    };
  });

  // Find max impact from the calculated data
  const maxImpact = Math.max(...hotspotsWithImpact.map((h) => h.impact));

  // Second pass: Calculate impact percentages
  return hotspotsWithImpact.map((hotspot) => ({
    ...hotspot,
    impactPercentage:
      maxImpact > 0
        ? Math.min(100, Math.round((hotspot.impact / maxImpact) * 100))
        : 0,
  }));
}

export function hotspotAnalysis(options = {}) {
  return {
    // Data
    drivers: options.drivers || [],

    // State
    isLoading: true,
    isAnalyzing: false,
    isCancelling: false,
    hasErrors: false,
    emptyStateVisible: !options.cached_results, // Only show empty state if no cached results
    errorMessage: "",
    currentAnalysisParams: null,
    results: null,
    hasAnalyzed: false,
    analysisCancelled: false, // Track if last analysis was cancelled
    analyzedDriver: "",
    analyzedAttributes: "",
    analyzedMinResponses: CONFIG.DEFAULT_MIN_RESPONSES,

    // Form reactive state
    selectedDriverId: "",
    selectedSquadIds: [],
    selectedTagIds: [],
    selectedAttributeIds: [],
    selectedHierarchyLevelIds: [],
    selectedBranch: "",
    minResponses: CONFIG.DEFAULT_MIN_RESPONSES,
    showAdvanced: false,
    filtersModalOpen: false,

    // Temporary modal state
    tempSelectedSquadIds: [],
    tempSelectedTagIds: [],
    tempSelectedAttributeIds: [],
    tempSelectedHierarchyLevelIds: [],
    tempMinResponses: CONFIG.DEFAULT_MIN_RESPONSES,

    // Chart filtering state
    activeFilter: "worst", // 'worst', 'best', or null

    // Table sorting state
    sortBy: "impact",
    sortReverse: false, // false = descending (high to low), true = ascending (low to high)

    // Progress state
    analysisProgress: 0,
    currentCombination: 0,
    totalCombinations: 0,
    currentValueCombination: 0,
    totalValueCombinations: 0,

    // Polling control
    pollingTimeoutId: null,
    isPolling: false,
    currentJobId: null,
    currentJobStatus: null, // Track actual job status from Redis

    // API Paths
    analyzePath: options.analyzePath,
    statusPath: options.statusPath,
    cancelPath: options.cancelPath,
    csvPath: options.csvPath,

    // COMPONENT INITIALIZATION
    init() {
      // Initialize query param persistence
      this.queryParamPersistence = initQueryParamPersistence("index_v2", [
        "driver_id",
        "attribute_ids",
        "min_responses",
        "squad_ids",
        "tag_ids",
        "hierarchy_level_ids",
        "branch",
      ]);

      // Component is initialized
      this.setupFormHandlers();

      // Listen for data requests from React
      document.addEventListener("hotspot:request-data", () => {
        if (this.hasResults) {
          this.updateChart();
        }
      });

      // Listen for branch changes from the SnapshotBranchFilter component
      document.addEventListener("react-state-update", (event) => {
        if (event.detail.branch !== undefined) {
          this.selectedBranch = event.detail.branch;
          this.updateUrlFromCurrentState();
        }
      });

      // Listen for page unload to cleanup polling
      window.addEventListener("beforeunload", () => {
        this.stopPolling();
      });

      // Listen for Alpine.js component destroy
      this.$el.addEventListener("x-destroyed", () => {
        this.stopPolling();
      });

      // Wait for URL restoration to complete before initializing
      this.queryParamPersistence.whenReady(() => {
        this.initializeFromUrlOrCache();
      });
    },

    initializeFromUrlOrCache() {
      this.isLoading = true;
      this.isAnalyzing = false;

      // Load URL params first (if any)
      const foundUrlParams = this.loadFromUrlParams();
      if (!foundUrlParams) {
        this.setDefaults();
      }

      // 1. Check for cached results first (based on current params)
      if (this.hasCachedResults()) {
        this.loadCachedResults();
        return;
      }

      // 2. Check for running job
      if (options.running_job_id) {
        this.pollExistingJob(options.running_job_id);
        return;
      }

      // 3. Start new analysis as fallback (if we have valid params)
      if (this.canAnalyze) {
        this.performAnalysis();
      } else {
        this.showEmptyState();
      }
    },

    // Helper methods for simplified initialization
    hasCachedResults() {
      return (
        options.cached_results &&
        !this.queryParamPersistence.wasRestoredFromStorage()
      );
    },

    loadCachedResults() {
      this.loadFromCachedResults(options.cached_results);
      this.isLoading = false;
      this.emptyStateVisible = false;

      // Ensure chart initialization after cached results are loaded
      this.$nextTick(() => {
        document.dispatchEvent(new CustomEvent("hotspot:chart-init"));
      });
    },

    pollExistingJob(jobId) {
      this.currentJobId = jobId;
      // Keep loading state until polling confirms job is actually running
      this.emptyStateVisible = false;
      this.pollJobStatus(jobId);
    },

    showEmptyState() {
      this.isLoading = false;
      this.emptyStateVisible = true;
    },

    setDefaults() {
      // Set default attributes if none selected
      if (
        this.selectedAttributeIds.length === 0 &&
        options.default_attribute_ids
      ) {
        this.selectedAttributeIds = options.default_attribute_ids.map((id) =>
          id.toString(),
        );
      }

      // Set default driver if none selected and drivers available
      if (!this.selectedDriverId && this.drivers.length > 0) {
        this.selectedDriverId = this.drivers[0].id.toString();
      }

      // Set default branch if none selected and default available
      if (!this.selectedBranch && options.default_branch) {
        this.selectedBranch = options.default_branch;
      }
    },

    // FORM & URL MANAGEMENT

    setupFormHandlers() {
      // Form validation and UI updates
      this.$nextTick(() => {
        this.updateFormValidation();
      });
    },

    // ANALYSIS EXECUTION

    async performAnalysis() {
      // Validate using Alpine.js state directly
      if (!this.selectedDriverId) {
        this.showError("Please select a driver.");
        return;
      }

      if (this.selectedAttributeIds.length < CONFIG.MIN_ATTRIBUTES_REQUIRED) {
        this.showError(
          `Please select at least ${CONFIG.MIN_ATTRIBUTES_REQUIRED} attributes.`,
        );
        return;
      }

      // Store analysis parameters for display using Alpine.js state
      this.analyzedDriver =
        this.drivers.find(
          (d) => d.id.toString() === this.selectedDriverId.toString(),
        )?.name || "Unknown Driver";
      this.analyzedAttributes =
        this.selectedAttributeIds.length + " attribute groups selected";
      this.analyzedMinResponses = this.minResponses;

      // Store params for CSV export and update URL
      this.currentAnalysisParams = {
        driver_id: this.selectedDriverId,
        attribute_ids: this.selectedAttributeIds.join(","),
        min_responses: this.minResponses,
        squad_ids: this.selectedSquadIds.join(",") || "",
        tag_ids: this.selectedTagIds.join(",") || "",
        hierarchy_level_ids: this.selectedHierarchyLevelIds.join(",") || "",
        branch: this.selectedBranch || "",
      };

      // Update URL to reflect current analysis - persistence utility will save to localStorage
      this.updateUrlFromCurrentState();

      // Show loading state while waiting for server response
      this.isLoading = true;
      this.analysisCancelled = false; // Clear cancelled flag when starting new analysis

      this.resetProgressState();

      // Prepare request data using Alpine.js state
      const requestData = new URLSearchParams();
      requestData.append("driver_id", this.selectedDriverId);
      requestData.append("attribute_ids", this.selectedAttributeIds.join(","));
      requestData.append("min_responses", this.minResponses);

      if (this.selectedSquadIds.length > 0) {
        requestData.append("squad_ids", this.selectedSquadIds.join(","));
      }

      if (this.selectedTagIds.length > 0) {
        requestData.append("tag_ids", this.selectedTagIds.join(","));
      }

      if (this.selectedHierarchyLevelIds.length > 0) {
        requestData.append(
          "hierarchy_level_ids",
          this.selectedHierarchyLevelIds.join(","),
        );
      }

      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]');
      if (csrfToken) {
        requestData.append("authenticity_token", csrfToken.content);
      }

      try {
        // Build URL with branch parameter if present
        let analyzeUrl = this.analyzePath;
        if (this.selectedBranch) {
          const separator = analyzeUrl.includes("?") ? "&" : "?";
          analyzeUrl += `${separator}branch=${encodeURIComponent(this.selectedBranch)}`;
        }

        // Make AJAX request
        const response = await fetch(analyzeUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: requestData,
        });

        const data = await response.json();

        if (data.success) {
          if (data.job_id) {
            // Background job was queued, wait for polling to confirm status
            this.currentJobId = data.job_id;
            // Keep loading state until first poll confirms job is running
            this.pollJobStatus(data.job_id);
          } else {
            // Results were cached, display immediately
            this.isLoading = false;
            this.hasAnalyzed = true;
            this.analysisCancelled = false; // Clear cancelled flag for cached results
            this.showResults(data.results);
          }
        } else {
          this.isLoading = false;
          this.showError(data.error || "Analysis failed. Please try again.");
        }
      } catch (error) {
        this.isLoading = false;
        console.error("Analysis error:", error);
        this.showError(
          "Network error. Please check your connection and try again.",
        );
      }
    },

    async cancelAnalysis() {
      if (!this.currentJobId) {
        console.error("No job ID available for cancellation");
        return;
      }

      this.isCancelling = true;

      try {
        // Build cancel URL
        let cancelUrl = this.cancelPath.replace(":job_id", this.currentJobId);
        if (this.selectedBranch) {
          const separator = cancelUrl.includes("?") ? "&" : "?";
          cancelUrl += `${separator}branch=${encodeURIComponent(this.selectedBranch)}`;
        }

        // Make DELETE request to cancel the job
        const response = await fetch(cancelUrl, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-Token": document
              .querySelector('meta[name="csrf-token"]')
              .getAttribute("content"),
          },
        });

        const data = await response.json();

        if (data.success) {
          // Cancel was successful
          this.stopPolling();
          this.resetAnalysisState();
          this.resetProgressState();
          this.analysisCancelled = true; // Mark analysis as cancelled
          console.log("Analysis cancelled successfully");
        } else if (data.error === "Job is not running") {
          // Job finished before we could cancel - treat as successful cancellation
          this.stopPolling();
          this.resetAnalysisState();
          this.resetProgressState();
          this.analysisCancelled = true; // Mark analysis as cancelled
          console.log("Analysis cancelled (job already finished)");
        } else {
          this.isCancelling = false;
          this.showError(
            data.error || "Failed to cancel analysis. Please try again.",
          );
        }
      } catch (error) {
        this.isCancelling = false;
        console.error("Cancel analysis error:", error);
        this.showError("Network error while cancelling. Please try again.");
      }
    },

    pollJobStatus(jobId) {
      let retryCount = 0;
      this.isPolling = true;
      this.currentJobId = jobId;

      const poll = () => {
        // Check if polling was stopped
        if (!this.isPolling) {
          console.log("Polling stopped, aborting poll for job", jobId);
          return;
        }

        // Build status URL with branch parameter if present
        let statusUrl = this.statusPath.replace(":job_id", jobId);
        if (this.selectedBranch) {
          const separator = statusUrl.includes("?") ? "&" : "?";
          statusUrl += `${separator}branch=${encodeURIComponent(this.selectedBranch)}`;
        }

        fetch(statusUrl, {
          headers: {
            "X-CSRF-Token": document
              .querySelector('meta[name="csrf-token"]')
              .getAttribute("content"),
          },
        })
          .then((response) => {
            if (
              response.status === 404 &&
              retryCount < CONFIG.MAX_INITIAL_RETRIES
            ) {
              // Handle race condition - job not found yet, retry shortly
              retryCount++;
              console.log(
                `Job not found yet, retrying (${retryCount}/${CONFIG.MAX_INITIAL_RETRIES})...`,
              );
              this.pollingTimeoutId = setTimeout(
                poll,
                CONFIG.INITIAL_RETRY_DELAY,
              );
              return;
            }
            return response.json();
          })
          .then((data) => {
            if (!data) return; // Skip if 404 retry above

            // Always track the current job status
            this.currentJobStatus = data.status;

            if (data.status === "complete") {
              this.stopPolling();
              this.resetAnalysisState();
              this.hasAnalyzed = true;
              this.analysisCancelled = false; // Clear cancelled flag on successful completion
              this.showResults(data.results);
            } else if (data.status === "cancelled") {
              this.stopPolling();
              this.resetAnalysisState();
              this.analysisCancelled = true; // Mark analysis as cancelled
              console.log("Analysis was cancelled");
              // Don't show error for cancelled jobs, just reset state
            } else if (data.status === "error" || !data.success) {
              this.stopPolling();
              this.resetAnalysisState();
              this.showError(
                data.error || "Analysis failed. Please try again.",
              );
            } else {
              // Job is queued or processing - now safe to show analyzing state
              if (data.status === "queued" || data.status === "processing") {
                this.isLoading = false;
                this.isAnalyzing = true;
              }

              // Update progress if available
              if (data.progress) {
                this.analysisProgress = data.progress.percentage || 0;
                this.currentCombination =
                  data.progress.current_combination || 0;
                this.totalCombinations = data.progress.total_combinations || 0;
                this.currentValueCombination =
                  data.progress.current_value_combination || 0;
                this.totalValueCombinations =
                  data.progress.total_value_combinations || 0;
              }

              // Still processing (queued or processing), continue polling
              this.pollingTimeoutId = setTimeout(poll, CONFIG.POLLING_INTERVAL);
            }
          })
          .catch((error) => {
            this.stopPolling();
            this.isAnalyzing = false;
            this.isLoading = false;
            this.currentJobId = null;
            this.currentJobStatus = null;
            this.showError(
              "Failed to check analysis status. Please refresh and try again.",
            );
            console.error("Error polling job status:", error);
          });
      };

      // Start polling immediately to catch quick cancellations
      poll();
    },

    // JOB POLLING & STATUS

    stopPolling() {
      this.isPolling = false;
      if (this.pollingTimeoutId) {
        clearTimeout(this.pollingTimeoutId);
        this.pollingTimeoutId = null;
      }
    },

    // Helper methods to reduce code duplication
    resetAnalysisState() {
      this.isAnalyzing = false;
      this.isLoading = false;
      this.isCancelling = false;
      this.currentJobId = null;
      this.currentJobStatus = null;
    },

    resetProgressState() {
      this.analysisProgress = 0;
      this.currentCombination = 0;
      this.totalCombinations = 0;
      this.currentValueCombination = 0;
      this.totalValueCombinations = 0;
    },

    // UI STATE MANAGEMENT

    showResults(results) {
      this.hideAllContainers();
      this.results = results;

      // Ensure the chart element is initialized after DOM updates
      this.$nextTick(() => {
        // Dispatch event to trigger chart initialization if needed
        document.dispatchEvent(new CustomEvent("hotspot:chart-init"));

        // Use consolidated chart update logic
        this.updateChart();
      });
    },

    showError(message) {
      this.hideAllContainers();
      this.errorMessage = message;
      this.hasErrors = true;
    },

    hideAllContainers() {
      this.results = null;
      this.isLoading = false;
      this.isAnalyzing = false;
      this.isCancelling = false;
      this.currentJobId = null;
      this.currentJobStatus = null;
      this.hasErrors = false;
      this.emptyStateVisible = false;
    },

    // DATA EXPORT

    exportResults() {
      if (!this.currentAnalysisParams) return;

      const params = new URLSearchParams(this.currentAnalysisParams);
      // Add the current filter to the export
      if (this.activeFilter) {
        params.set("filter", this.activeFilter);
      }
      const csvUrl = this.csvPath + "?" + params.toString();

      // Open in new window to force download
      // Create a temporary link to trigger download
      const link = document.createElement("a");
      link.href = csvUrl;
      link.download = `hotspot-analysis-${new Date().toISOString().slice(0, 10)}.csv`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // FILTER & SORT MANAGEMENT

    updateFormValidation() {
      // Update form validation state reactively
      this.$nextTick(() => {
        // This will trigger reactive updates in the template via computed properties
      });
    },

    handleSquadChange(squadIds) {
      this.selectedSquadIds = squadIds;
      this.updateUrlFromCurrentState();
    },

    handleTagChange(tagIds) {
      this.selectedTagIds = tagIds;
      this.updateUrlFromCurrentState();
    },

    handleHierarchyLevelChange(hierarchyLevelIds) {
      this.selectedHierarchyLevelIds = hierarchyLevelIds;
      this.updateUrlFromCurrentState();
    },

    handleDriverChange() {
      this.updateUrlFromCurrentState();
    },

    // Update URL to reflect current form state
    // This is different from the old updateUrlParams - it only updates, never clears
    updateUrlFromCurrentState() {
      // Only update URL if we have valid analysis parameters
      if (
        this.selectedDriverId &&
        this.selectedAttributeIds.length >= CONFIG.MIN_ATTRIBUTES_REQUIRED
      ) {
        const url = new URL(window.location);
        url.searchParams.set("driver_id", this.selectedDriverId);
        url.searchParams.set(
          "attribute_ids",
          this.selectedAttributeIds.join(","),
        );
        url.searchParams.set("min_responses", this.minResponses);

        if (this.selectedSquadIds.length > 0) {
          url.searchParams.set("squad_ids", this.selectedSquadIds.join(","));
        } else {
          url.searchParams.delete("squad_ids");
        }

        if (this.selectedTagIds.length > 0) {
          url.searchParams.set("tag_ids", this.selectedTagIds.join(","));
        } else {
          url.searchParams.delete("tag_ids");
        }

        if (this.selectedBranch) {
          url.searchParams.set("branch", this.selectedBranch);
        } else {
          url.searchParams.delete("branch");
        }

        if (this.selectedHierarchyLevelIds.length > 0) {
          url.searchParams.set(
            "hierarchy_level_ids",
            this.selectedHierarchyLevelIds.join(","),
          );
        } else {
          url.searchParams.delete("hierarchy_level_ids");
        }

        // Update URL
        window.history.replaceState({}, "", url);

        // Save to localStorage
        this.queryParamPersistence.saveParams();

        // Update analysis params for consistency
        this.currentAnalysisParams = {
          driver_id: this.selectedDriverId,
          attribute_ids: this.selectedAttributeIds.join(","),
          min_responses: this.minResponses,
          squad_ids: this.selectedSquadIds.join(",") || "",
          tag_ids: this.selectedTagIds.join(",") || "",
          hierarchy_level_ids: this.selectedHierarchyLevelIds.join(",") || "",
          branch: this.selectedBranch || "",
        };
      }
    },

    // Filter methods
    setFilter(filterType) {
      this.activeFilter = filterType;
      // Reset sort to default when changing filters
      this.sortBy = "impact";
      this.sortReverse = false;

      // Update chart when filter changes
      this.updateChart();
    },

    // Dispatch custom event to notify React of data changes
    updateChart() {
      // Wait for DOM updates to complete
      this.$nextTick(() => {
        const chartElement = document.getElementById("hotspot-scatter-chart");

        // Log the current state for debugging

        // Dispatch custom event with the data
        const event = new CustomEvent("hotspot:data-updated", {
          detail: {
            data: this.allHotspotTableData,
            activeFilter: this.activeFilter,
            hasResults: this.hasResults,
            overallAverage: this.results?.overall_average || 0,
          },
          bubbles: true,
        });

        // Dispatch from the chart element if it exists, otherwise from document
        if (chartElement) {
          chartElement.dispatchEvent(event);
        } else {
          console.warn("Chart element not found, dispatching from document");
          document.dispatchEvent(event);
        }

        console.debug(
          "Dispatched hotspot:data-updated event with",
          this.allHotspotTableData?.length || 0,
          "data points",
        );
      });
    },

    applyActiveFilter(data) {
      if (!this.activeFilter) return data;

      if (this.activeFilter === "worst") {
        return data.filter((item) => item.diff < 0);
      } else if (this.activeFilter === "best") {
        return data.filter((item) => item.diff > 0);
      }

      return data;
    },

    sortHotspotData(data) {
      // Unified sorting logic for all columns
      const sorted = [...data].sort((a, b) => {
        let aVal, bVal;

        switch (this.sortBy) {
          case "impact":
            aVal = a.impact;
            bVal = b.impact;
            break;
          case "score":
            aVal = a.score;
            bVal = b.score;
            break;
          case "responses":
            aVal = a.responses;
            bVal = b.responses;
            break;
          case "diff":
            aVal = a.diff;
            bVal = b.diff;
            break;
          case "dimension":
            aVal = a.dimension.toLowerCase();
            bVal = b.dimension.toLowerCase();
            return this.sortReverse
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          case "values":
            // Join arrays for comparison, handle both old and new format
            aVal = a.values
              .map((v) => v.value || v)
              .join(" ")
              .toLowerCase();
            bVal = b.values
              .map((v) => v.value || v)
              .join(" ")
              .toLowerCase();
            return this.sortReverse
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          default:
            return 0;
        }

        // Apply sort direction consistently for numeric values
        // sortReverse: false = high to low (descending), true = low to high (ascending)
        return this.sortReverse ? aVal - bVal : bVal - aVal;
      });

      return sorted;
    },

    resetAnalysis() {
      this.hasAnalyzed = false;
      this.hideAllContainers();
      this.emptyStateVisible = true; // Show empty state when resetting
      // URL params will be managed by the persistence utility
    },

    selectAllAttributes() {
      if (options.all_attribute_ids) {
        this.selectedAttributeIds = options.all_attribute_ids.map((id) =>
          id.toString(),
        );
      }
    },

    toggleAllAttributes() {
      if (this.allAttributesSelected) {
        // Deselect all
        this.selectedAttributeIds = [];
      } else {
        // Select all
        this.selectAllAttributes();
      }
    },

    selectAllHierarchyLevels() {
      if (options.all_hierarchy_level_ids) {
        this.selectedHierarchyLevelIds = options.all_hierarchy_level_ids.map(
          (id) => id.toString(),
        );
      }
    },

    toggleAllHierarchyLevels() {
      if (this.allHierarchyLevelsSelected) {
        // Deselect all
        this.selectedHierarchyLevelIds = [];
      } else {
        // Select all
        this.selectAllHierarchyLevels();
      }
    },

    handleAttributeChange() {
      this.updateUrlFromCurrentState();
    },

    handleMinResponsesChange() {
      this.updateUrlFromCurrentState();
    },

    // Modal state management
    openFiltersModal() {
      // Copy current state to temporary state when modal opens
      this.tempSelectedSquadIds = [...this.selectedSquadIds];
      this.tempSelectedTagIds = [...this.selectedTagIds];
      this.tempSelectedAttributeIds = [...this.selectedAttributeIds];
      this.tempSelectedHierarchyLevelIds = [...this.selectedHierarchyLevelIds];
      this.tempMinResponses = this.minResponses;

      // Sync store with current state so modal filters show correct values
      this.syncStoreWithMainState();

      this.filtersModalOpen = true;
    },

    applyModalChanges() {
      // Apply temporary state to main state
      this.selectedSquadIds = [...this.tempSelectedSquadIds];
      this.selectedTagIds = [...this.tempSelectedTagIds];
      this.selectedAttributeIds = [...this.tempSelectedAttributeIds];
      this.selectedHierarchyLevelIds = [...this.tempSelectedHierarchyLevelIds];
      this.minResponses = this.tempMinResponses;

      // Update store to reflect applied changes
      this.syncStoreWithMainState();

      // Update URL and form validation
      this.updateUrlFromCurrentState();
      this.updateFormValidation();

      // Close modal
      this.filtersModalOpen = false;
    },

    cancelModalChanges() {
      // Restore store to main state values, discarding any changes made in modal
      this.syncStoreWithMainState();

      // Just close modal without applying changes
      // Temp state will be re-copied from main state next time modal opens
      this.filtersModalOpen = false;
    },

    // Temporary modal state handlers
    handleTempSquadChange(squadIds) {
      this.tempSelectedSquadIds = squadIds;
    },

    handleTempTagChange(tagIds) {
      this.tempSelectedTagIds = tagIds;
    },

    handleTempHierarchyLevelChange(hierarchyLevelIds) {
      this.tempSelectedHierarchyLevelIds = hierarchyLevelIds;
    },

    toggleAllTempAttributes() {
      if (this.allTempAttributesSelected) {
        // Deselect all
        this.tempSelectedAttributeIds = [];
      } else {
        // Select all
        if (options.all_attribute_ids) {
          this.tempSelectedAttributeIds = options.all_attribute_ids.map((id) =>
            id.toString(),
          );
        }
      }
    },

    toggleAllTempHierarchyLevels() {
      if (this.allTempHierarchyLevelsSelected) {
        // Deselect all
        this.tempSelectedHierarchyLevelIds = [];
      } else {
        // Select all
        if (options.all_hierarchy_level_ids) {
          this.tempSelectedHierarchyLevelIds =
            options.all_hierarchy_level_ids.map((id) => id.toString());
        }
      }
    },

    loadFromCachedResults(cachedResults) {
      // Populate form with cached params first (while form is still visible)
      const params = cachedResults.url_params;
      this.populateFormFromParams(params);

      // Store params for CSV export
      this.currentAnalysisParams = {
        driver_id: params.driver_id,
        attribute_ids: params.attribute_ids.join
          ? params.attribute_ids.join(",")
          : params.attribute_ids,
        min_responses: params.min_responses,
        squad_ids: params.squad_ids || "",
        tag_ids: params.tag_ids || "",
        hierarchy_level_ids: params.hierarchy_level_ids || "",
        branch: params.branch || "",
      };

      // Set analyzed labels from cached results metadata
      if (cachedResults.metadata) {
        this.analyzedDriver =
          cachedResults.metadata.driver_name || "Unknown Driver";
        this.analyzedMinResponses =
          cachedResults.metadata.min_responses || CONFIG.DEFAULT_MIN_RESPONSES;

        // Use attribute names from server response
        if (
          cachedResults.metadata.attribute_names &&
          cachedResults.metadata.attribute_names.length > 0
        ) {
          this.analyzedAttributes =
            cachedResults.metadata.attribute_names.join(", ");
        } else {
          this.analyzedAttributes = `${cachedResults.metadata.attribute_count || 0} attribute groups selected`;
        }
      }

      this.hasAnalyzed = true;
      this.showResults(cachedResults);
    },

    loadFromUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const driverId = urlParams.get("driver_id");

      if (driverId) {
        const params = {
          driver_id: parseInt(driverId),
          attribute_ids: urlParams.get("attribute_ids")
            ? urlParams.get("attribute_ids").split(",").map(Number)
            : [],
          min_responses:
            parseInt(urlParams.get("min_responses")) ||
            CONFIG.DEFAULT_MIN_RESPONSES,
          squad_ids: urlParams.get("squad_ids") || "",
          tag_ids: urlParams.get("tag_ids") || "",
          hierarchy_level_ids: urlParams.get("hierarchy_level_ids") || "",
          branch: urlParams.get("branch") || "",
        };

        this.populateFormFromParams(params);

        // Set currentAnalysisParams to prevent URL from being cleared
        // This is critical - without this, any form interaction will clear the URL
        this.currentAnalysisParams = {
          driver_id: params.driver_id,
          attribute_ids: params.attribute_ids.join(","),
          min_responses: params.min_responses,
          squad_ids: params.squad_ids,
          tag_ids: params.tag_ids,
          hierarchy_level_ids: params.hierarchy_level_ids,
          branch: params.branch,
        };

        return true; // Indicate that params were found
      }

      return false; // No params found
    },

    populateFormFromParams(params) {
      // Set reactive properties directly - Alpine.js will update the DOM
      if (params.driver_id) {
        this.selectedDriverId = params.driver_id.toString();
      }

      if (params.squad_ids) {
        // Handle both string and array formats
        if (Array.isArray(params.squad_ids)) {
          this.selectedSquadIds = params.squad_ids.map((id) => id.toString());
        } else if (params.squad_ids) {
          this.selectedSquadIds = params.squad_ids.split(",").filter(Boolean);
        }
      }

      if (params.attribute_ids && params.attribute_ids.length > 0) {
        this.selectedAttributeIds = params.attribute_ids.map((id) =>
          id.toString(),
        );
      }

      if (params.min_responses) {
        this.minResponses = params.min_responses;
      }

      if (params.tag_ids) {
        this.selectedTagIds = params.tag_ids.split(",").filter(Boolean);
      }

      if (params.hierarchy_level_ids) {
        this.selectedHierarchyLevelIds = params.hierarchy_level_ids
          .split(",")
          .filter(Boolean);
      }

      if (params.branch) {
        this.selectedBranch = params.branch;
      }

      // Force form validation and DOM update after populating
      this.$nextTick(() => {
        this.updateFormValidation();
      });
    },

    // Helper function to sync store with current main state
    syncStoreWithMainState() {
      if (this.$store.snapshotResultFilters) {
        this.$store.snapshotResultFilters.squadIds = [...this.selectedSquadIds];
        this.$store.snapshotResultFilters.tagIds = [...this.selectedTagIds];
        this.$store.snapshotResultFilters.branch = this.selectedBranch;
      }
    },

    resetFilters() {
      // Reset all filter values to defaults
      this.selectedSquadIds = [];
      this.selectedTagIds = [];
      this.selectedHierarchyLevelIds = [];
      this.selectedBranch = "";
      this.minResponses = CONFIG.DEFAULT_MIN_RESPONSES;

      // Reset selected attributes to defaults
      if (options.default_attribute_ids) {
        this.selectedAttributeIds = options.default_attribute_ids.map((id) =>
          id.toString(),
        );
      } else {
        this.selectedAttributeIds = [];
      }

      // Update the store to reflect reset state
      this.syncStoreWithMainState();

      // Update form validation state
      this.updateFormValidation();

      // Update URL and localStorage to reflect reset state
      this.updateUrlFromCurrentState();
    },

    // Sorting functionality
    isSortOrder(order) {
      return this.sortBy === order;
    },

    changeSortBy(column) {
      if (this.sortBy === column) {
        // Toggle direction for same column
        this.sortReverse = !this.sortReverse;
      } else {
        // New column - impact defaults to desc (sortReverse=false), others to asc (sortReverse=true)
        this.sortBy = column;
        // For impact, we want high to low by default (sortReverse=false)
        // For other columns, we want low to high by default (sortReverse=true)
        this.sortReverse = column !== "impact";
      }
    },
    // COMPUTED PROPERTIES
    get hasResults() {
      return this.results !== null;
    },

    get shouldShowEmptyState() {
      return !this.hasResults && !this.isLoading;
    },

    get shouldShowNoHotspotsFound() {
      return this.hasResults && this.hotspotsFound === 0;
    },

    get shouldShowChart() {
      return this.hasResults && this.hotspotsFound > 0;
    },

    get hotspotsFound() {
      return this.results?.hotspots_found || 0;
    },

    get hotspotTableData() {
      if (!this.results?.hotspots) return [];

      // Use helper function to calculate data
      const data = calculateHotspotData(
        this.results.hotspots,
        this.results.overall_average,
      );

      // Sort the data first
      const sortedData = this.sortHotspotData(data);

      // Apply filter
      return this.applyActiveFilter(sortedData);
    },

    get allHotspotTableData() {
      // Unfiltered version for chart data
      if (!this.results?.hotspots) return [];

      // Use helper function to calculate data
      const data = calculateHotspotData(
        this.results.hotspots,
        this.results.overall_average,
      );

      return this.sortHotspotData(data);
    },

    get canAnalyze() {
      return (
        this.selectedDriverId !== "" && this.selectedAttributeIds.length >= 2
      );
    },

    get canCancel() {
      return (
        this.isAnalyzing &&
        (this.currentJobStatus === "queued" ||
          this.currentJobStatus === "processing")
      );
    },

    get activeFiltersCount() {
      let count = 0;
      // Count each selected team
      count += this.selectedSquadIds.length;
      // Count each selected attribute/tag
      count += this.selectedTagIds.length;
      // Count each selected hierarchy level
      count += this.selectedHierarchyLevelIds.length;
      // Count min responses if not default
      if (this.minResponses !== CONFIG.DEFAULT_MIN_RESPONSES) count++;

      // Count non-default selected attributes
      if (options.default_attribute_ids) {
        const defaultAttributeIds = options.default_attribute_ids.map((id) =>
          id.toString(),
        );
        const nonDefaultAttributes = this.selectedAttributeIds.filter(
          (id) => !defaultAttributeIds.includes(id),
        );
        count += nonDefaultAttributes.length;

        // Also count if any default attributes were deselected
        const deselectedDefaults = defaultAttributeIds.filter(
          (id) => !this.selectedAttributeIds.includes(id),
        );
        count += deselectedDefaults.length;
      }

      return count;
    },

    get isDirty() {
      // Check if current selections differ from last analyzed values
      if (!this.hasAnalyzed) return true; // Always dirty if never analyzed
      if (this.analysisCancelled) return true; // Always dirty if last analysis was cancelled

      // Check if driver changed
      if (this.currentAnalysisParams?.driver_id !== this.selectedDriverId)
        return true;

      // Check if attribute IDs changed
      const lastAttributeIds =
        this.currentAnalysisParams?.attribute_ids?.split(",") || [];
      if (lastAttributeIds.length !== this.selectedAttributeIds.length)
        return true;
      if (
        !lastAttributeIds.every((id) => this.selectedAttributeIds.includes(id))
      )
        return true;

      // Check if squad IDs changed
      const lastSquadIds =
        this.currentAnalysisParams?.squad_ids?.split(",").filter((id) => id) ||
        [];
      if (lastSquadIds.length !== this.selectedSquadIds.length) return true;
      if (!lastSquadIds.every((id) => this.selectedSquadIds.includes(id)))
        return true;

      // Check if tag IDs changed
      const lastTagIds =
        this.currentAnalysisParams?.tag_ids?.split(",").filter((id) => id) ||
        [];
      if (lastTagIds.length !== this.selectedTagIds.length) return true;
      if (!lastTagIds.every((id) => this.selectedTagIds.includes(id)))
        return true;

      // Check if hierarchy level IDs changed
      const lastHierarchyLevelIds =
        this.currentAnalysisParams?.hierarchy_level_ids
          ?.split(",")
          .filter((id) => id) || [];
      if (
        lastHierarchyLevelIds.length !== this.selectedHierarchyLevelIds.length
      )
        return true;
      if (
        !lastHierarchyLevelIds.every((id) =>
          this.selectedHierarchyLevelIds.includes(id),
        )
      )
        return true;

      // Check if min responses changed
      if (
        this.currentAnalysisParams?.min_responses !==
        this.minResponses.toString()
      )
        return true;

      // Check if branch changed
      if (
        (this.currentAnalysisParams?.branch || "") !==
        (this.selectedBranch || "")
      )
        return true;

      return false;
    },

    get allAttributesSelected() {
      if (
        !options.all_attribute_ids ||
        options.all_attribute_ids.length === 0
      ) {
        return false;
      }
      return (
        options.all_attribute_ids.length === this.selectedAttributeIds.length &&
        options.all_attribute_ids.every((id) =>
          this.selectedAttributeIds.includes(id.toString()),
        )
      );
    },

    get allHierarchyLevelsSelected() {
      if (
        !options.all_hierarchy_level_ids ||
        options.all_hierarchy_level_ids.length === 0
      ) {
        return false;
      }
      return (
        options.all_hierarchy_level_ids.length ===
          this.selectedHierarchyLevelIds.length &&
        options.all_hierarchy_level_ids.every((id) =>
          this.selectedHierarchyLevelIds.includes(id.toString()),
        )
      );
    },

    get allTempAttributesSelected() {
      if (
        !options.all_attribute_ids ||
        options.all_attribute_ids.length === 0
      ) {
        return false;
      }
      return (
        options.all_attribute_ids.length ===
          this.tempSelectedAttributeIds.length &&
        options.all_attribute_ids.every((id) =>
          this.tempSelectedAttributeIds.includes(id.toString()),
        )
      );
    },

    get allTempHierarchyLevelsSelected() {
      if (
        !options.all_hierarchy_level_ids ||
        options.all_hierarchy_level_ids.length === 0
      ) {
        return false;
      }
      return (
        options.all_hierarchy_level_ids.length ===
          this.tempSelectedHierarchyLevelIds.length &&
        options.all_hierarchy_level_ids.every((id) =>
          this.tempSelectedHierarchyLevelIds.includes(id.toString()),
        )
      );
    },

    hasInitialFilterState() {
      // Check if driver is selected (required for analysis)
      if (!this.selectedDriverId) return false;

      // Check if attribute IDs match defaults
      if (
        !options.default_attribute_ids ||
        options.default_attribute_ids.length === 0
      ) {
        return false;
      }

      const defaultAttributeIds = options.default_attribute_ids.map((id) =>
        id.toString(),
      );
      if (this.selectedAttributeIds.length !== defaultAttributeIds.length) {
        return false;
      }
      if (
        !this.selectedAttributeIds.every((id) =>
          defaultAttributeIds.includes(id),
        )
      ) {
        return false;
      }

      // Check if other filters are in their default state
      if (this.selectedSquadIds.length > 0) return false;
      if (this.selectedTagIds.length > 0) return false;
      if (this.selectedHierarchyLevelIds.length > 0) return false;
      if (this.selectedBranch !== (options.default_branch || "")) return false;
      if (this.minResponses !== CONFIG.DEFAULT_MIN_RESPONSES) return false;

      return true;
    },
  };
}
